import type { IdbDatabase } from './idb-database';

export interface IdbStoreRecord {
  key: string;
}

export interface IdbObjectStoreIndex<T extends IdbStoreRecord> {
  keyPath: keyof T & string;
  unique?: boolean;
}

export interface IdbObjectStoreConfig<T extends IdbStoreRecord> {
  database: IdbDatabase;
  storeName: string;
  indexes?: IdbObjectStoreIndex<T>[];
}

/**
 * A wrapper around an IndexedDB object store that provides a simple async API for basic CRUD operations, as well as some additional helper methods for deleting records by index or predicate. The store is automatically registered with the provided database upon instantiation.
 * You can use this class as a base for more specific object stores that manage particular types of records, by extending it and adding any additional methods needed for your use case.
 * For an example of a blob store with similar functionality, see {@link SymbolIdbCache}.
 */
export abstract class IdbObjectStore<T extends IdbStoreRecord> {
  private readonly database: IdbDatabase;
  private readonly storeName: string;

  constructor(config: IdbObjectStoreConfig<T>) {
    this.database = config.database;
    this.storeName = config.storeName;

    config.database.registerStore({
      storeName: config.storeName,
      indexes: config.indexes ?? [],
    });
  }

  /**
   * Ensures the underlying database is open and ready for operations. This method should be called before performing any operations on the store, to ensure that the database connection is established. In most cases, you can call this method once during your application's initialization phase, and then safely perform store operations afterwards without needing to await the database connection each time, as the connection will be reused.
   */
  async ready(): Promise<void> {
    await this.database.open();
  }

  /**
   * Retrieves a record from the store by its key. If the record exists, it is returned as a promise that resolves to the record object; if the record does not exist, the promise resolves to undefined.
   * @param key
   */
  async get(key: string): Promise<T | undefined> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readonly');
    const store = transaction.objectStore(this.storeName);
    const record = await this.promisifyRequest(store.get(key)) as T | undefined;

    await this.awaitTransaction(transaction);

    return record;
  }

  /**
   * Adds or updates a record in the store. The record must include a 'key' property, which serves as the unique identifier for the record in the store. If a record with the same key already exists, it will be overwritten; if not, a new record will be created.
   * @param record
   */
  async put(record: T): Promise<void> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readwrite');

    transaction.objectStore(this.storeName).put(record);
    await this.awaitTransaction(transaction);
  }

  /**
   * Deletes a record from the store by its key. If the record exists, it will be removed; if the record does not exist, the operation will have no effect.
   * @param key
   */
  async delete(key: string): Promise<void> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readwrite');

    transaction.objectStore(this.storeName).delete(key);
    await this.awaitTransaction(transaction);
  }

  /**
   * Deletes records from the store by an index value. All records matching the specified index value will be removed.
   * @param indexName
   * @param value
   * @returns An array of keys for the records that were removed.
   */
  async deleteByIndex(indexName: string, value: IDBValidKey): Promise<string[]> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index(indexName);
    const cursorRequest = index.openCursor(IDBKeyRange.only(value));

    const removed: string[] = [];

    await new Promise<void>((resolve, reject) => {
      cursorRequest.onsuccess = () => {
        try {
          const cursor = cursorRequest.result;

          if (cursor) {
            const record = cursor.value as T;

            removed.push(record.key);
            cursor.delete();
            cursor.continue();
          }
          else {
            resolve();
          }
        }
        catch (error) {
          reject(error);
        }
      };

      cursorRequest.onerror = () => reject(cursorRequest.error);
    });

    await this.awaitTransaction(transaction);

    return removed;
  }

  /**
   * Deletes records from the store that match the specified predicate function. All records for which the predicate returns true will be removed.
   * @param predicate A function that takes a record and returns true if the record should be deleted.
   * @returns An array of keys for the records that were removed.
   */
  async deleteWhere(predicate: (record: T) => boolean): Promise<string[]> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const cursorRequest = store.openCursor();

    const removed: string[] = [];

    await new Promise<void>((resolve, reject) => {
      cursorRequest.onsuccess = () => {
        try {
          const cursor = cursorRequest.result;

          if (cursor) {
            const record = cursor.value as T;

            if (predicate(record)) {
              removed.push(record.key);
              cursor.delete();
            }

            cursor.continue();
          }
          else {
            resolve();
          }
        }
        catch (error) {
          reject(error);
        }
      };

      cursorRequest.onerror = () => reject(cursorRequest.error);
    });

    await this.awaitTransaction(transaction);

    return removed;
  }

  /**
   * Clears all records from the store. This operation removes all records without deleting the store itself.
   */
  async clear(): Promise<void> {
    const database = await this.database.open();
    const transaction = database.transaction(this.storeName, 'readwrite');

    transaction.objectStore(this.storeName).clear();
    await this.awaitTransaction(transaction);
  }

  /**
   * Awaits the completion of a transaction. Resolves when the transaction completes successfully, and rejects if the transaction encounters an error or is aborted.
   * @param transaction The IDBTransaction to await.
   */
  private awaitTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error ?? new Error('IDB transaction aborted'));
    });
  }

  /**
   * Promisifies an IDBRequest, allowing it to be used with async/await. Resolves with the request result when successful, and rejects with the request error if it fails.
   * @param request The IDBRequest to promisify.
   */
  private promisifyRequest<R>(request: IDBRequest<R>): Promise<R> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
